// src/components/coordinator/GoalRulesEditor.js
//
// The rules section of the Edit goal pop-up (admins). Each rule reads as a
// sentence: Every [N] [what] in [where] = [P] point(s). Admins can add as many
// as they like, reorder them (checked top to bottom; each item counts once)
// and remove them. Rule logic: services/goalRules.js.
// Own classes (goal-rule-*) with light-mode rules in index.css.

import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2, Search, Info } from "lucide-react";
import { DISEASE_OPTIONS } from "../../constants/diseaseOptions";
import {
  RULE_CATEGORIES,
  SCOPE_TYPES,
  blankRule,
  ruleText,
  studiesOfTypes,
  validateRule,
} from "../../services/goalRules";
import { monthLabel } from "../../services/enrollmentPeriods";

const INPUT =
  "goal-rule-input px-2 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 text-sm text-slate-100 focus:outline-none focus:border-emerald-400/50 focus:ring-2 focus:ring-emerald-500/20 transition [color-scheme:dark]";
const SMALL_BTN =
  "goal-rule-btn inline-flex items-center justify-center w-7 h-7 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-slate-300 transition disabled:opacity-40";

const studyLabel = (s) => [s.STUDY_ID, s.NAME].filter(Boolean).join(" – ") || "(unnamed study)";

const Chip = ({ on, onClick, children, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={on}
    aria-label={label}
    className={`goal-rule-chip ${on ? "goal-rule-chip--on" : ""} px-2 py-1 rounded-lg border text-[11px] font-semibold transition`}
  >
    {children}
  </button>
);

const StudyPicker = ({ studies, selected, onChange }) => {
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const shown = studies.filter((s) => !needle || studyLabel(s).toLowerCase().includes(needle));
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div className="mt-2">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search studies…"
          className={`${INPUT} w-full pl-7`}
          aria-label="Search studies"
        />
      </div>
      <ul className="mt-1.5 max-h-36 overflow-y-auto space-y-0.5 pr-1" aria-label="Studies">
        {shown.map((s) => (
          <li key={s.id}>
            <label className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-white/[0.04] text-[12px] text-slate-200 cursor-pointer">
              <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggle(s.id)} />
              <span className="truncate">{studyLabel(s)}</span>
              {s.STATUS && <span className="ml-auto text-[10px] text-slate-500 shrink-0">{s.STATUS}</span>}
            </label>
          </li>
        ))}
        {shown.length === 0 && <li className="px-2 py-1 text-[11px] text-slate-400">No studies match.</li>}
      </ul>
    </div>
  );
};

const RuleCard = ({ rule, index, total, studies, studiesById, onChange, onMove, onRemove }) => {
  const set = (patch) => onChange({ ...rule, ...patch });
  const setScope = (patch) => onChange({ ...rule, scope: { ...rule.scope, ...patch } });
  const toggleCategory = (id) =>
    set({ categories: rule.categories.includes(id) ? rule.categories.filter((c) => c !== id) : [...rule.categories, id] });
  const errors = validateRule(rule);
  const matching = rule.scope.type === SCOPE_TYPES.TYPES ? studiesOfTypes(rule.scope.types, studies) : [];

  return (
    <li className="goal-rule-card rounded-xl border border-white/10 bg-white/[0.03] p-3" aria-label={`Rule ${index + 1}`}>
      <div className="flex items-start gap-2">
        <span className="goal-rule-num mt-1 w-5 h-5 shrink-0 rounded-full bg-emerald-500/20 text-[10px] font-bold text-emerald-200 flex items-center justify-center">
          {index + 1}
        </span>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-1.5 text-sm text-slate-200">
            <span>Every</span>
            <input
              type="number"
              min={1}
              step={1}
              value={rule.count || ""}
              onChange={(e) => set({ count: e.target.value === "" ? 0 : Number(e.target.value) })}
              className={`${INPUT} w-16`}
              aria-label="How many"
            />
            {RULE_CATEGORIES.map((c) => (
              <Chip key={c.id} on={rule.categories.includes(c.id)} onClick={() => toggleCategory(c.id)}>
                {c.label}
              </Chip>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-sm text-slate-200">
            <span>in</span>
            <select
              value={rule.scope.type}
              onChange={(e) => setScope({ type: e.target.value })}
              className={INPUT}
              aria-label="Which studies"
            >
              <option value={SCOPE_TYPES.ANY} className="bg-[#0b0820]">any study</option>
              <option value={SCOPE_TYPES.STUDIES} className="bg-[#0b0820]">these studies…</option>
              <option value={SCOPE_TYPES.TYPES} className="bg-[#0b0820]">studies of type…</option>
            </select>
            <span>=</span>
            <input
              type="number"
              min={1}
              step={1}
              value={rule.points || ""}
              onChange={(e) => set({ points: e.target.value === "" ? 0 : Number(e.target.value) })}
              className={`${INPUT} w-16`}
              aria-label="Points"
            />
            <span>point{rule.points === 1 ? "" : "s"}</span>
          </div>

          {rule.scope.type === SCOPE_TYPES.STUDIES && (
            <StudyPicker
              studies={studies}
              selected={rule.scope.studyDocIds}
              onChange={(ids) => setScope({ studyDocIds: ids })}
            />
          )}
          {rule.scope.type === SCOPE_TYPES.TYPES && (
            <div>
              <div className="flex flex-wrap gap-1.5">
                {DISEASE_OPTIONS.map((t) => (
                  <Chip
                    key={t}
                    on={rule.scope.types.includes(t)}
                    onClick={() =>
                      setScope({
                        types: rule.scope.types.includes(t)
                          ? rule.scope.types.filter((x) => x !== t)
                          : [...rule.scope.types, t],
                      })
                    }
                  >
                    {t}
                  </Chip>
                ))}
              </div>
              {rule.scope.types.length > 0 && (
                <p className="mt-1.5 text-[11px] text-slate-400">
                  {matching.length ? `Matches now: ${matching.map(studyLabel).join(", ")}` : "No studies of this type yet."}
                </p>
              )}
            </div>
          )}

          <p className="goal-rule-sentence text-[12px] font-medium text-emerald-200">{ruleText(rule, studiesById)}</p>
          {errors.length > 0 && <p className="goal-rule-error text-[11px] text-rose-300">{errors.join(" ")}</p>}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <button type="button" className={SMALL_BTN} onClick={() => onMove(-1)} disabled={index === 0} aria-label="Move up">
            <ArrowUp className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            className={SMALL_BTN}
            onClick={() => onMove(1)}
            disabled={index === total - 1}
            aria-label="Move down"
          >
            <ArrowDown className="w-3.5 h-3.5" />
          </button>
          <button type="button" className={SMALL_BTN} onClick={onRemove} aria-label="Remove rule">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </li>
  );
};

/**
 * rules: the draft list; onChange(nextRules). inheritedFrom: "YYYY-MM" when
 * these rules were set in an earlier month. loading/error: the rule sets' load.
 */
const GoalRulesEditor = ({ rules = [], onChange, monthId, inheritedFrom, studiesById, loading, error }) => {
  const studies = useMemo(
    () =>
      [...(studiesById ? studiesById.values() : [])].sort((a, b) => studyLabel(a).localeCompare(studyLabel(b))),
    [studiesById]
  );
  const update = (i, rule) => onChange(rules.map((r, j) => (j === i ? rule : r)));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= rules.length) return;
    const next = [...rules];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <section aria-label="Goal rules" className="mt-5">
      <div className="flex flex-wrap items-baseline gap-x-2 mb-1">
        <h3 className="text-[11px] uppercase tracking-[0.2em] font-semibold text-white">
          Rules (from {monthLabel(monthId) || monthId} on)
        </h3>
        {inheritedFrom && (
          <span className="text-[11px] text-slate-400">
            Using rules set in {monthLabel(inheritedFrom) || inheritedFrom}; saving makes these the rules from{" "}
            {monthLabel(monthId) || monthId} on.
          </span>
        )}
      </div>
      <p className="goal-rule-note flex items-start gap-1.5 text-[11px] text-slate-400 mb-3">
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span>
          Turns coordinators' weekly reports into points (an admin approves before anything changes). Checked top to
          bottom; each item counts once, so put specific rules first.
        </span>
      </p>
      {loading ? (
        <p className="text-sm text-slate-400">Loading rules…</p>
      ) : error ? (
        <p className="text-sm text-rose-300">{error}</p>
      ) : (
        <>
          {rules.length === 0 ? (
            <p className="text-sm text-slate-400 rounded-xl border border-dashed border-white/10 px-3 py-3 text-center">
              No rules. Points come only from hand-entered counts.
            </p>
          ) : (
            <ol className="space-y-2">
              {rules.map((r, i) => (
                <RuleCard
                  key={r.id}
                  rule={r}
                  index={i}
                  total={rules.length}
                  studies={studies}
                  studiesById={studiesById}
                  onChange={(next) => update(i, next)}
                  onMove={(d) => move(i, d)}
                  onRemove={() => onChange(rules.filter((_, j) => j !== i))}
                />
              ))}
            </ol>
          )}
          <button
            type="button"
            onClick={() => onChange([...rules, blankRule()])}
            className="goal-rule-add mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-400/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-xs font-semibold text-emerald-200 transition"
          >
            <Plus className="w-3.5 h-3.5" />
            Add rule
          </button>
        </>
      )}
    </section>
  );
};

export default GoalRulesEditor;
